import { createFreePreviewMember } from 'actions/LuxPlusActions'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import SolidTagAltIcon from 'LuxKit/Icons/solid/SolidTagAltIcon'
import SolidUnlockAltIcon from 'LuxKit/Icons/solid/SolidUnlockAltIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import type { ReactElement } from 'react'
import React, { Fragment, useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'

interface BenefitItem {
  icon: ReactElement
  title: React.ReactNode
  description: React.ReactNode
}

const BENEFIT_ITEMS: Array<BenefitItem> = [
  {
    icon: <SolidTagAltIcon />,
    title: <FormattedMessage
      id="LTL3JoxKRye0824YS-AE"
      defaultMessage="Member only pricing"
      description="Benefit title for member pricing on LuxPlus+ Month benefits section"
    />,
    description: <FormattedMessage
      id="FbtZjQM2QyJrvox9G9vA"
      defaultMessage="Save hundreds on hotel offers, tours and local experiences"
      description="Benefit description for member pricing savings on LuxPlus+ Month"
    />,
  },
  {
    icon: <SolidStarIcon />,
    title: <FormattedMessage
      id="hVDTgm5Oyfx9ft1nnJ_j"
      defaultMessage="Bonus inclusions"
      description="Benefit title for bonus inclusions on LuxPlus+ Month benefits section"
    />,
    description: <FormattedMessage
      id="d9UUsGOsNSdLS7CfhejI"
      defaultMessage="Hotel credits, daily breakfast, room upgrades, on-board cruises credit and more"
      description="Benefit description for bonus inclusions on LuxPlus+ Month"
    />,
  },
  {
    icon: <SolidUnlockAltIcon />,
    title: <FormattedMessage
      id="1EcJdcQDcvceyilR5pKh"
      defaultMessage="Exclusive offers"
      description="Benefit title for exclusive offers on LuxPlus+ Month benefits section"
    />,
    description: <FormattedMessage
      id="23eFTuvQQ6t0i1H3YiHr"
      defaultMessage="Unlock hidden offers and early access to our best offers - only for LuxPlus+ members"
      description="Benefit description for exclusive offers on LuxPlus+ Month"
    />,
  },
]

const BenefitsContainer = styled.div`
  display: grid;
  grid-template: auto 1px auto 1px auto / 1fr;
  gap: ${rem(16)};
  ${setSvgIconSize(24)};

  ${mediaQueryUp.tablet} {
    --item-w: minmax(auto, ${rem(310)});
    grid-template: auto / var(--item-w) 1px var(--item-w) 1px var(--item-w);
    gap: ${rem(32)};
    justify-content: center;
    ${setSvgIconSize(48)};
  }
`

const BenefitDivider = styled(Divider)`
  ${mediaQueryUp.tablet} {
    height: ${rem(126)};
  }
`

function LuxPlusMonthLandingPageBenefits() {
  const intl = useIntl()
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const startFreePreview = useCallback(async() => {
    if (!isLoggedIn) {
      const loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (loginResult.loggedIn) {
        dispatch(createFreePreviewMember())
      }
    } else {
      dispatch(createFreePreviewMember())
    }
  }, [dispatch, isLoggedIn, showModal])

  if (canViewLuxPlusBenefits) return null

  return <Sheet padding="48 0">
    <LayoutContainer>
      <Group direction="vertical" gap={40}>
        <Heading variant="heading2" align="center">
          <FormattedMessage
            id="D3dzndozqeeVDM5z_7Xg"
            defaultMessage="Year-round <i>benefits</i> with {programName}"
            description="Heading for year-round benefits section on LuxPlus+ Month landing page"
            values={{
              i: (chunks) => <i>{chunks}</i>,
              programName: <Colour value="lux-plus">{LUXURY_PLUS.PROGRAM_NAME}</Colour>,
            }}
          />
        </Heading>
        <BenefitsContainer>
          {BENEFIT_ITEMS.map((benefitItem, index) => <Fragment key={index}>
            {index !== 0 && <BenefitDivider kind="primary" direction="horizontal" tabletDirection="vertical" />}
            <Group direction="vertical" horizontalAlign="center" gap={12}>
              {benefitItem.icon}
              <Group direction="vertical" gap={4}>
                <Heading variant="heading5" align="center">{benefitItem.title}</Heading>
                <BodyText variant="medium" align="center">{benefitItem.description}</BodyText>
              </Group>
            </Group>
          </Fragment>)}
        </BenefitsContainer>
        {!canViewLuxPlusBenefits && <Group direction="vertical" tabletHorizontalAlign="center">
          <TextButton kind="primary" size="large" shape="round" onClick={startFreePreview}>
            {intl.formatMessage({
              id: 'draJOgD8NVIMKEpfwYTu',
              defaultMessage: 'Start your FREE preview today',
              description: 'Call-to-action button to start free LuxPlus+ preview on benefits section',
            })}
          </TextButton>
        </Group>}
      </Group>
    </LayoutContainer>
  </Sheet>
}

export default LuxPlusMonthLandingPageBenefits
